import { useState } from "react";
import { CreditCardModel, ContactModel } from "@/apidtos";
import {
  CreditCardForm,
  CreditCardFormValues,
} from "@phoenix/payments/components/CreditCardForm";
import { useApi } from "@core/api";
import { LoadingOverlay } from "@core/components/ui/loading-overlay";
import { useTranslation } from "react-i18next";
import { Alert, AlertDescription } from "@core/components/ui/alert";
import { PaymentContactsProvider } from "@phoenix/payments/contexts/PaymentContactsContext";
import { ReplaceIfExtension } from "@core/components/PipelineComponents/ReplaceIfExtension";
import { useAddressBook } from "@core/hooks/useAddressBook";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DialogTrigger } from "@core/components/ui/dialog";
import { SiteAddressModal } from "@/overrides/site/components/Address/SiteAddressModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPenToSquare } from "@fortawesome/pro-light-svg-icons";

interface CreateCreditCardModalProps {
  onSuccess: (paymentMethodId?: number) => void;
}

function isAddressValidationError(msg: string): boolean {
  const lower = msg.toLowerCase();
  return lower.includes("state is required") || lower.includes("country is required") || lower.includes("missing required contact info");
}

export const CreateCreditCardModal = (props: CreateCreditCardModalProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [addressError, setAddressError] = useState(false);
  const [failedContactId, setFailedContactId] = useState<number | null>(null);

  const api = useApi();
  const { t } = useTranslation();
  const addressBook = useAddressBook();
  const queryClient = useQueryClient();
  const contacts: ContactModel[] = addressBook.data?.map((c: any) => c.Secondary!).filter(Boolean) ?? [];

  const contactToEdit = failedContactId != null
    ? contacts.find(c => c.Id === failedContactId) ?? null
    : null;

  const updateAddressMutation = useMutation({
    mutationFn: async (contact: ContactModel) => {
      const newCustomerContactId = await api["/addressbook/update"]({ ...contact, IsActive: true, IsDeleted: false });
      addressBook.reload();
      queryClient.invalidateQueries({ queryKey: ["addressBook"] });
      const updatedBook = await queryClient.fetchQuery({
        queryKey: ["addressBook"],
        queryFn: () => api["/addressbook"](),
      });
      const newEntry = (updatedBook as any[])?.find((x) => x.Id === newCustomerContactId);
      return newEntry?.SecondaryId ?? newEntry?.Secondary?.Id ?? 0;
    },
    onSuccess: (newContactId) => {
      setAddressError(false);
      setFailedContactId(newContactId || null);
      setErrorMsg(t("ui.addressUpdated", "Address updated. Please try submitting again."));
    },
  });

  const create = async (data: CreditCardFormValues) => {
    setAddressError(false);
    setFailedContactId(null);
    const [ month, year ] = data.cardExpiration.split("/");
    const contactId = data.billingContactId ?? contacts[0]?.Id;
    if (!contactId) {
      setErrorMsg(
        t("ui.billingContactRequired", "Must supply a billing contact to store a payment method")
      );
      return;
    }
    const model: CreditCardModel = {
      CardNumber: data.cardNumber.replaceAll(" ", ""),
      CardHolderName: data.cardHolderName,
      CardExpirationMonth: parseInt(month.replaceAll(" ", "")),
      CardExpirationYear: parseInt(year.replaceAll(" ", "")),
      BillingContactId: contactId,
    };
    setLoading(true);
    try {
      const r = await api.AddCurrentUserPaymentMethod(data.name!, model);
      if (!r.IsSuccess) {
        throw new Error(r.Messages?.join(", "));
      }
      props.onSuccess(r.Result);
    } catch (err) {
      const msg = (err as Error)?.message || "";
      if (isAddressValidationError(msg)) {
        setAddressError(true);
        setFailedContactId(data.billingContactId ?? contacts[0]?.Id ?? null);
      }
      setErrorMsg(msg || t("ui.AnUnknownErrorOccurred", "An unknown error occurred"));
    } finally {
      setLoading(false);
    }
  };

  if (addressBook.isLoading) {
    return <LoadingOverlay />;
  }

  return (
    <div className="relative">
      {loading && <LoadingOverlay />}
      {errorMsg && (
        <Alert variant="danger" className="mt-2">
          <AlertDescription>
            {addressError ? (
              <div className="flex flex-col gap-2">
                <p>{t("ui.billingAddressIncomplete", "The billing address is missing required fields (State and/or Country). Please update the address and try again.")}</p>
                <SiteAddressModal
                  contact={contactToEdit ?? undefined}
                  onConfirm={(updated) => updateAddressMutation.mutate(updated)}
                >
                  <DialogTrigger asChild>
                    <button type="button" className="inline-flex items-center gap-1 text-sm underline cursor-pointer">
                      <FontAwesomeIcon icon={faPenToSquare} />
                      {t("ui.editBillingAddress", "Edit Billing Address")}
                    </button>
                  </DialogTrigger>
                </SiteAddressModal>
              </div>
            ) : (
              errorMsg
            )}
          </AlertDescription>
        </Alert>
      )}
      <PaymentContactsProvider contacts={contacts}>
        <ReplaceIfExtension name="payments.create-card-modal" onSuccess={props.onSuccess}>
          <CreditCardForm
            includeName
            includeContactSelect={true}
            onSubmit={create} />
        </ReplaceIfExtension>
      </PaymentContactsProvider>
    </div>
  );
};
